/**
 * Header state and the props shared by the desktop-class and mobile headers (DIS-1840).
 * No React import: SiteHeader, DesktopHeader and MobileHeader all read this file.
 */
import type {OrderLine} from '../../transport/apiServiceResponse';

/**
 * Store state picks the variant, scroll depth picks full or compact (DIS-1840 Q4, ruling 5:
 * minimal compact is the non-ecommerce store's compact header).
 * - default: ecommerce store, full header (desktop 62:1675, 112; mobile 65:4145, 152).
 * - collapsed: ecommerce store, full compact (desktop 77:6182, 56; mobile 77:5859, 92).
 * - noEcom: non-ecommerce store, full header (desktop 475:24517, 56; mobile 475:24534, 92).
 * - noEcomCollapsed: non-ecommerce store, minimal compact (desktop 77:7310, 56; mobile
 *   77:7341, 92). Same as noEcom less the account button.
 */
export type HeaderState = 'default' | 'collapsed' | 'noEcom' | 'noEcomCollapsed';

export const isEcomState = (state: HeaderState): boolean =>
  state === 'default' || state === 'collapsed';

export const isCompactState = (state: HeaderState): boolean =>
  state === 'collapsed' || state === 'noEcomCollapsed';

export type Availability = 'both' | 'pickup' | 'delivery' | 'inStore';

export const AVAILABILITY_COPY: Readonly<Record<Availability, string>> = Object.freeze({
  both: 'Pick Up & Delivery Available',
  pickup: 'Pick Up Available',
  delivery: 'Delivery Available',
  inStore: 'In Store Only',
});

/** No store selected: BrandHeader's fallback copy (BrandHeader.tsx StoreNameCard). */
export const NO_STORE_COPY = 'Your Store';

/** Gift-card store: the store selector's plain text (R42; legacy BrandHeader "GIFT CARD STORE"). */
export const GIFT_CARD_STORE_COPY = 'Gift Card Store';

/**
 * Press handlers the header calls. Task A10 binds them to routes and stores. No menu press:
 * SiteHeader opens its own menu sheet (DIS-1898 B9, ruling R60).
 */
export type HeaderActions = {
  onLogoPress(): void;
  /** The mobile compact search icon: opens SearchScreen with no keywords. */
  onSearchPress(): void;
  /** Enter or "View All Results" in the header search (DIS-1907): SearchScreen with keywords. */
  onSearchSubmit(keywords: string): void;
  /** A header search suggestion card (DIS-1907): the product page. */
  onSearchResultPress(line: OrderLine): void;
  onStorePress(): void;
  onCartPress(): void;
  /** Set only on a gift-card store with gift cards enabled; the header shows "Back to store". */
  onBackToStorePress?(): void;
};
